import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { images } from "../data";

export default function Julefrokost() {
  const [selectedImg, setSelectedImg] = useState(null);

  return (
    <div className="bg-white min-h-screen pt-20 font-sans">
      
      {/* Hero Block */}
      <section 
        className="relative h-[40vh] md:h-[50vh] bg-cover bg-center"
        style={{ backgroundImage: `url(${images.subpageHero})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
             <span className="text-primary tracking-widest text-sm uppercase mb-4 block">Højtiden på Spisehuset</span>
             <h1 className="text-4xl md:text-6xl text-white font-serif tracking-wide text-shadow-sm">Julefrokost</h1>
          </motion.div>
        </div>
      </section>

      {/* Content Block */}
      <section className="py-16 md:py-24 bg-white text-gray-800">
         <div className="container mx-auto px-6">
            <div className="max-w-none">
              <section className="mb-20">
                <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center">En uforglemmelig julefrokost i Nordsjælland</h2>
                <div className="md:columns-2 gap-12 leading-relaxed font-light text-gray-700">
                  <p className="mb-6">
                    Hos <span className="text-primary font-medium">JJfoodsolutions</span> tilbyder vi en julefrokost i Nordsjælland, som dine gæster vil huske længe. Med 30 års erfaring i cateringbranchen ved vi, hvordan man skaber den perfekte feststemning med lækker mad og en hyggelig atmosfære. Vores dygtige kokke sammensætter en menu, der passer til både private og erhverv, og vi sørger for, at der er noget for enhver smag.
                  </p>
                  <p className="mb-6">
                    Vi ved, at ingen julefrokost er ens. Derfor tilbyder vi skræddersyede løsninger, hvor du selv kan vælge retterne til din julefrokost i Nordsjælland. Vores julemenuer inkluderer traditionelle retter som sild, leverpostej, flæskesteg og risalamande, men også spændende nyfortolkninger, der vil overraske og glæde dine gæster. Vi bruger kun de bedste råvarer for at sikre den højeste kvalitet og den bedste smag.
                  </p>
                </div>
              </section>

              <section className="mb-20">
                <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center">Fleksible løsninger til alle behov</h2>
                <div className="md:columns-2 gap-12 leading-relaxed font-light text-gray-700">
                  <p className="mb-6">
                    Når du vælger JJfoodsolutions til din julefrokost, får du ikke kun lækker mad, men også professionel service. Vores erfarne team står klar til at hjælpe med alt fra planlægning til afvikling, så du kan slappe af og nyde festen sammen med dine gæster. Vi sørger for smuk opdækning og en hyggelig atmosfære, der skaber den perfekte ramme om din julefrokost.
                  </p>
                  <p className="mb-6">
                    Uanset om du planlægger en lille, intim julefrokost eller en stor firmafest, har vi løsninger, der passer til dine behov. Vores cateringservice kan klargøre maden til afhentning, eller du kan vælge at afholde festen i vores hyggelige spisehus. Vi er fleksible og tilpasser os dine ønsker, så din julefrokost på Nordsjælland bliver præcis, som du drømmer om.
                  </p>
                  <p className="mb-6">
                    Vil du kombinere din julefrokost med en komplet oplevelse, kan du også se vores <Link to="/selskaber-og-events" className="text-primary hover:underline">selskabslokaler i Værløse</Link>, bestille <Link to="/takeaway-catering" className="text-primary hover:underline">mad ud af huset</Link>, eller opleve stemningen på <Link to="/spisehuset" className="text-primary hover:underline">Restaurant Værløse</Link>.
                  </p>
                </div>
              </section>

              <section className="text-center pt-12 border-t border-gray-100">
                <h2 className="text-3xl md:text-4xl font-serif mb-8">Kontakt os i dag for at høre mere</h2>
                <p className="max-w-2xl mx-auto text-gray-700 font-light mb-10">
                  Vil du høre mere om, hvordan vi kan hjælpe dig med at arrangere den perfekte julefrokost i Nordsjælland? Vores professionelle ansatte sidder klar til at tage imod din henvendelse.
                </p>
                <div className="flex flex-col md:flex-row justify-center items-center gap-8 text-lg">
                  <a href="tel:+4542952240" className="text-dark hover:text-primary transition-colors flex items-center gap-2">
                    (+45) 42 95 22 40
                  </a>
                  <a href="mailto:jjfoodsolution@outlook.com" className="text-dark hover:text-primary transition-colors flex items-center gap-2 underline underline-offset-4">
                    jjfoodsolution@outlook.com
                  </a>
                </div>
                <div className="mt-12">
                  <a href="/kontakt" className="inline-block px-12 py-4 bg-primary text-white hover:bg-dark transition-all duration-300 tracking-widest text-sm uppercase">Kontakt os</a>
                </div>
              </section>
            </div>
         </div>
      </section>

      {/* Gallery Block */}
      <section className="py-16 md:py-24 bg-bg-light border-t border-gray-100">
         <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="text-3xl font-serif text-center mb-12">Stemning & Billeder</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { src: "/imgi_13_confitering-scaled.jpeg", alt: "Julemad" },
                { src: "/imgi_3_billede17.jpeg", alt: "Julefrokost" },
                { src: "/imgi_4_6x6a2250-websize.jpeg", alt: "Anretning" },
                { src: "/imgi_5_6x6a3472-websize.jpeg", alt: "Stemning" }
              ].map((img, index) => (
                <div key={index} className="overflow-hidden bg-gray-100 group">
                  <img 
                    src={img.src} 
                    alt={img.alt} 
                    className="w-full h-[170px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700 cursor-pointer" 
                    onClick={() => setSelectedImg(img.src)}
                  />
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* Lightbox Pop-up */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setSelectedImg(null)}
          >
            <motion.img 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImg} 
              alt="Forstørret visning" 
              className="max-w-full max-h-[90vh] object-contain shadow-2xl"
            />
            <button 
              className="absolute top-10 right-10 text-white text-4xl font-light hover:text-primary transition-colors"
              onClick={() => setSelectedImg(null)}
            >
              &times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
